import React, { useState, useEffect } from "react";
import { BarChart3, Filter, Download, Activity } from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import type { Case } from "../../types";
import Button from "../../components/common/ui/button/Button";
import { useOrg } from "../../contexts/OrgContext.tsx";
import { AuditLogEntry, AuditLogPagination } from "../../types/audit";
import Pagination from "../../components/common/ui/pagination/Pagination";
import ActivityLogItem from "../../components/common/activity/ActivityLogItem";

interface AuditLogDashboardProps {}

export const AuditLogDashboard: React.FC<AuditLogDashboardProps> = () => {
  const { setAlert } = useAlert();

  const [cases, setCases] = useState<Case[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const { orgId } = useOrg();

  // Filters
  const [selectedCase, setSelectedCase] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [selectedResourceType, setSelectedResourceType] = useState<string>("");
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [showExternalOnly, setShowExternalOnly] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<AuditLogPagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Export modal
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    if (orgId) {
      loadCases(orgId);
    }
  }, [orgId]);

  useEffect(() => {
    if (orgId) {
      loadAuditLogs();
    }
  }, [
    orgId,
    currentPage,
    selectedCase,
    selectedUser,
    selectedResourceType,
    selectedAction,
    dateFrom,
    dateTo,
    showExternalOnly,
  ]);

  const loadCases = async (orgId: string) => {
    try {
      const casesList = await apiClient.listCases(orgId);
      setCases(casesList);
    } catch (error) {
      console.error("Failed to load cases:", error);
    }
  };

  const loadAuditLogs = async () => {
    if (!orgId) return;

    setLoading(true);
    try {
      const response = await apiClient.getAuditLogs({
        organizationId: orgId,
        caseId: selectedCase || undefined,
        userId: selectedUser || undefined,
        resourceType: selectedResourceType || undefined,
        action: selectedAction || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        isExternalAccess: showExternalOnly || undefined,
        page: currentPage,
        limit: 20,
      });

      setAuditLogs(response.logs);
      setPagination(response.pagination);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to load audit logs",
      });
      setAuditLogs([]);
      setPagination({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportLogs = async (
    exportType: "current_page" | "all_matching"
  ) => {
    if (!orgId) return;

    setShowExportModal(false);
    setLoading(true);

    try {
      const blob = await apiClient.exportAuditLogs({
        organizationId: orgId,
        caseId: selectedCase || undefined,
        userId: selectedUser || undefined,
        resourceType: selectedResourceType || undefined,
        action: selectedAction || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        isExternalAccess: showExternalOnly || undefined,
        page: exportType === "current_page" ? currentPage : undefined,
        limit: exportType === "current_page" ? 20 : undefined,
        exportType,
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-logs-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setAlert({
        variant: "success",
        title: "Success",
        message: "Audit logs exported successfully",
      });
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to export audit logs",
      });
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedCase("");
    setSelectedUser("");
    setSelectedResourceType("");
    setSelectedAction("");
    setDateFrom("");
    setDateTo("");
    setShowExternalOnly(false);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="mb-2 flex items-center gap-3 text-title-md2 font-semibold text-black dark:text-white">
            <Activity size={32} />
            Audit Log
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Monitor all activities and access across your organization
          </p>
        </div>
        <Button
          onClick={() => setShowExportModal(true)}
          startIcon={<Download size={20} />}
        >
          Export Logs
        </Button>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-boxdark">
            <h3 className="mb-4 text-title-md font-semibold text-black dark:text-white">
              Export Audit Logs
            </h3>
            <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
              Choose what you want to export:
            </p>
            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={() => handleExportLogs("current_page")}
                className="w-full justify-start"
              >
                <div className="text-left">
                  <div className="font-semibold">Current Page Only</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Export {auditLogs.length} logs from this page
                  </div>
                </div>
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExportLogs("all_matching")}
                className="w-full justify-start"
              >
                <div className="text-left">
                  <div className="font-semibold">All Matching Filters</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Export up to 10,000 logs matching current filters (Total:{" "}
                    {pagination.total})
                  </div>
                </div>
              </Button>
            </div>
            <div className="mt-6 flex justify-end">
              <Button
                variant="outline"
                onClick={() => setShowExportModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-title-sm font-semibold text-black dark:text-white">
            <Filter size={20} />
            Filters
          </h2>
          <Button variant="outline" size="sm" onClick={clearFilters}>
            Clear All
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Case Filter */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Case
            </label>
            <select
              value={selectedCase}
              onChange={(e) => setSelectedCase(e.target.value)}
              disabled={!orgId}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">All Cases</option>
              {cases.map((caseItem) => (
                <option key={caseItem.id} value={caseItem.id}>
                  {caseItem.title}
                </option>
              ))}
            </select>
          </div>

          {/* Resource Type Filter */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Resource Type
            </label>
            <select
              value={selectedResourceType}
              onChange={(e) => setSelectedResourceType(e.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              <option value="">All Types</option>
              <option value="file">File</option>
              <option value="form">Form</option>
              <option value="case">Case</option>
              <option value="user">User</option>
              <option value="organization">Organization</option>
              <option value="share_link">Share Link</option>
            </select>
          </div>

          {/* Action Filter */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Action
            </label>
            <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              <option value="">All Actions</option>
              <option value="create">Create</option>
              <option value="upload">Upload</option>
              <option value="download">Download</option>
              <option value="view">View</option>
              <option value="share">Share</option>
              <option value="delete">Delete</option>
              <option value="invite">Invite</option>
              <option value="access-granted">Access Granted</option>
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Date From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Date To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>
        </div>

        {/* External Access Only Checkbox */}
        <div className="mt-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={showExternalOnly}
              onChange={(e) => setShowExternalOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-900"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Show external access only (share links)
            </span>
          </label>
        </div>
      </div>

      {/* Audit Log Entries */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
        <h2 className="mb-6 text-title-sm font-semibold text-black dark:text-white">
          Activity Log ({pagination.total} total)
        </h2>

        {loading ? (
          <p className="py-12 text-center text-sm text-gray-600 dark:text-gray-400">
            Loading...
          </p>
        ) : auditLogs.length === 0 ? (
          <div className="py-12 text-center">
            <BarChart3
              size={64}
              className="mx-auto mb-4 opacity-30 text-gray-400"
            />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              No audit log entries found matching your filters
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {auditLogs.map((log) => (
              <ActivityLogItem key={log.id} entry={log} variant="audit" />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && auditLogs.length > 0 && pagination.totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              pageSize={pagination.limit}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </div>
    </div>
  );
};
